import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.string().url(),
    DATABASE_URL_READ_REPLICA: z.string().url().optional(),
    NODE_ENV: z.enum(["development", "test", "production"]),
    NEXTAUTH_SECRET:
      process.env.NODE_ENV === "production"
        ? z.string().min(1)
        : z.string().min(1).optional(),
    NEXTAUTH_URL: z.preprocess(
      // This makes Vercel deployments not fail if you don't set NEXTAUTH_URL
      // Since NextAuth.js automatically uses the VERCEL_URL if present.
      (str) => process.env.VERCEL_URL ?? str,
      // VERCEL_URL doesn't include `https` so it cant be validated as a URL
      process.env.VERCEL ? z.string().min(1) : z.string().url(),
    ),
    // Add `.min(1) on ID and SECRET if you want to make sure they're not empty
    GOOGLE_CLIENT_ID: z.string(),
    ANTHROPIC_API_KEY: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    SLACK_SIGNING_SECRET: z.string(),
    LOOPS_API_KEY: z.string(),
    TWILIO_ACCOUNT_SID: z.string(),
    TWILIO_AUTH_TOKEN: z.string(),
    MOTHER_DUCK_ACCESS_TOKEN: z.string(),
    MISTRAL_API_KEY: z.string(),
    TWILIO_PHONE_NUMBER_A: z.string(),
    TWILIO_PHONE_NUMBER_B: z.string(),
    RESEND_API_KEY: z.string(),
    QSTASH_CURRENT_SIGNING_KEY: z.string(),
    GRAPH_TENANT_ID: z.string(),
    GRAPH_CLIENT_ID: z.string(),
    GRAPH_CLIENT_SECRET: z.string(),
    QSTASH_NEXT_SIGNING_KEY: z.string(),
    QSTASH_TOKEN: z.string(),
    QSTASH_URL: z.string(),
    PROXY_CURL_API_KEY: z.string(),
    OPENAI_ORG_ID: z.string(),
    OPENAI_API_KEY: z.string(),
    NANGO_SECRET_KEY: z.string(),
    BROWSERLESS_API_KEY: z.string(),
    ABLY_API_KEY: z.string(),
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_PREMIUM_PRICE_CODE: z.string(),
    UPSTASH_REDIS_TOKEN: z.string(),
    OUTLOOK_NOTIFICATION_SECRET: z.string(),
    UPSTASH_REDIS_URL: z.string(),
    UPSTASH_REDIS_CACHE_URL: z.string(),
    UPSTASH_REDIS_CACHE_TOKEN: z.string(),
    AWS_SECRET_ACCESS_KEY: z.string(),
    AWS_ACCESS_KEY_ID: z.string(),
    AWS_ACCOUNT_ID: z.string(),
    AWS_INGRESS_EMAIL_DOMAIN: z.string(),
    AUTH0_CLIENT_SECRET: z.string(),
    AUTH0_CLIENT_ID: z.string(),
    AUTH0_DOMAIN: z.string(),
    AUTH0_AUDIENCE: z.string(),
    PERPLEXITY_API_KEY: z.string(),
    RECALL_AI_API_KEY: z.string(),
    SENTRY_ORG: z.string(),
    SENTRY_PROJECT: z.string(),
    MAINTENANCE_MODE: z.enum(["ON", "OFF"]),
    FIRE_CRAWL_API_KEY: z.string(),
    ATTIO_WEBHOOK_API_SECRET: z.string(),
    PEOPLE_DATA_LABS_API_KEY: z.string(),
    UPSTASH_VECTOR_URL: z.string(),
    UPSTASH_VECTOR_TOKEN: z.string(),
    GOOGLE_CLOUD_PROJECT_ID: z.string(),
    EXA_API_KEY: z.string(),
    TRIGGER_SECRET_KEY: z.string(),
    VAPI_SECRET_KEY: z.string(),
    RECALL_AI_SIGNING_SECRET: z.string(),
    GOOGLE_SHEETS_WRITER_TYPE: z.string(),
    GOOGLE_SHEETS_WRITER_PROJECT_ID: z.string(),
    GOOGLE_SHEETS_WRITER_PRIVATE_KEY_ID: z.string(),
    GOOGLE_SHEETS_WRITER_PRIVATE_KEY: z.string(),
    GOOGLE_SHEETS_WRITER_CLIENT_EMAIL: z.string(),
    GOOGLE_SHEETS_WRITER_CLIENT_ID: z.string(),
    GOOGLE_SHEETS_WRITER_AUTH_URI: z.string(),
    GOOGLE_SHEETS_WRITER_TOKEN_URI: z.string(),
    GOOGLE_SHEETS_WRITER_AUTH_PROVIDER_X509_CERT_URL: z.string(),
    GOOGLE_SHEETS_WRITER_CLIENT_X509_CERT_URL: z.string(),
    GOOGLE_SHEETS_WRITER_UNIVERSE_DOMAIN: z.string(),
    GOOGLE_SERVICE_ACCOUNT_EMAIL: z.string(),
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY_ID: z.string(),
    GOOGLE_SERVICE_ACCOUNT_TYPE: z.string(),
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY: z.string(),
    GOOGLE_SERVICE_ACCOUNT_CLIENT_ID: z.string(),
    GOOGLE_SERVICE_ACCOUNT_AUTH_URI: z.string(),
    GOOGLE_SERVICE_ACCOUNT_TOKEN_URI: z.string(),
    GOOGLE_SERVICE_ACCOUNT_AUTH_PROVIDER_X509_CERT_URL: z.string(),
    GOOGLE_SERVICE_ACCOUNT_X509_CERT_URL: z.string(),
    GOOGLE_SERVICE_ACCOUNT_UNIVERSE_DOMAIN: z.string(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_TRPC_LOGGER_LINK_ENABLED: z.string().optional(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_LIVE: z.string().min(1),
    NEXT_PUBLIC_NGROK_URL: z.string().min(1),
    NEXT_PUBLIC_SENTRY_DSN: z.string().min(1),
    NEXT_PUBLIC_AXIOM_DATASET: z.string().min(1),
    NEXT_PUBLIC_AXIOM_TOKEN: z.string().min(1),
    NEXT_PUBLIC_SLACK_USER_FEEDBACK_HOOK_URL: z.string().min(1),
    NEXT_PUBLIC_PRODUCTION_DOMAIN: z.string().min(1),
    NEXT_PUBLIC_VAPI_PUBLIC_KEY: z.string(),

    NEXT_PUBLIC_POSTHOG_KEY: z.string(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string(),
    NEXT_PUBLIC_POSTHOG_ENABLED: z.coerce.boolean().optional().default(false),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NEXT_PUBLIC_TRPC_LOGGER_LINK_ENABLED:
      process.env.NEXT_PUBLIC_TRPC_LOGGER_LINK_ENABLED,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_URL_READ_REPLICA: process.env.DATABASE_URL_READ_REPLICA,
    NODE_ENV: process.env.NODE_ENV,
    NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET,
    TWILIO_ACCOUNT_SID: process.env.TWILIO_ACCOUNT_SID,
    TWILIO_AUTH_TOKEN: process.env.TWILIO_AUTH_TOKEN,
    TWILIO_PHONE_NUMBER_A: process.env.TWILIO_PHONE_NUMBER_A,
    TWILIO_PHONE_NUMBER_B: process.env.TWILIO_PHONE_NUMBER_B,
    NEXT_PUBLIC_VAPI_PUBLIC_KEY: process.env.NEXT_PUBLIC_VAPI_PUBLIC_KEY,
    VAPI_SECRET_KEY: process.env.VAPI_SECRET_KEY,
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    QSTASH_CURRENT_SIGNING_KEY: process.env.QSTASH_CURRENT_SIGNING_KEY,
    QSTASH_NEXT_SIGNING_KEY: process.env.QSTASH_NEXT_SIGNING_KEY,
    QSTASH_TOKEN: process.env.QSTASH_TOKEN,
    QSTASH_URL: process.env.QSTASH_URL,
    OPENAI_ORG_ID: process.env.OPENAI_ORG_ID,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    LOOPS_API_KEY: process.env.LOOPS_API_KEY,
    NANGO_SECRET_KEY: process.env.NANGO_SECRET_KEY,
    BROWSERLESS_API_KEY: process.env.BROWSERLESS_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    UPSTASH_VECTOR_URL: process.env.UPSTASH_VECTOR_URL,
    UPSTASH_VECTOR_TOKEN: process.env.UPSTASH_VECTOR_TOKEN,
    ABLY_API_KEY: process.env.ABLY_API_KEY,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_LIVE:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY_LIVE,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    NEXT_PUBLIC_NGROK_URL: process.env.NEXT_PUBLIC_NGROK_URL,
    STRIPE_PREMIUM_PRICE_CODE: process.env.STRIPE_PREMIUM_PRICE_CODE,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    SENTRY_ORG: process.env.SENTRY_ORG,
    SENTRY_PROJECT: process.env.SENTRY_PROJECT,
    UPSTASH_REDIS_TOKEN: process.env.UPSTASH_REDIS_TOKEN,
    UPSTASH_REDIS_URL: process.env.UPSTASH_REDIS_URL,
    UPSTASH_REDIS_CACHE_URL: process.env.UPSTASH_REDIS_CACHE_URL,
    UPSTASH_REDIS_CACHE_TOKEN: process.env.UPSTASH_REDIS_CACHE_TOKEN,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AWS_ACCOUNT_ID: process.env.AWS_ACCOUNT_ID,
    AWS_INGRESS_EMAIL_DOMAIN: process.env.AWS_INGRESS_EMAIL_DOMAIN,
    NEXT_PUBLIC_AXIOM_DATASET: process.env.NEXT_PUBLIC_AXIOM_DATASET,
    NEXT_PUBLIC_AXIOM_TOKEN: process.env.NEXT_PUBLIC_AXIOM_TOKEN,
    NEXT_PUBLIC_SLACK_USER_FEEDBACK_HOOK_URL:
      process.env.NEXT_PUBLIC_SLACK_USER_FEEDBACK_HOOK_URL,
    NEXT_PUBLIC_PRODUCTION_DOMAIN: process.env.NEXT_PUBLIC_PRODUCTION_DOMAIN,
    AUTH0_CLIENT_SECRET: process.env.AUTH0_CLIENT_SECRET,
    AUTH0_CLIENT_ID: process.env.AUTH0_CLIENT_ID,
    AUTH0_DOMAIN: process.env.AUTH0_DOMAIN,
    AUTH0_AUDIENCE: process.env.AUTH0_AUDIENCE,
    PERPLEXITY_API_KEY: process.env.PERPLEXITY_API_KEY,
    MAINTENANCE_MODE: process.env.MAINTENANCE_MODE,
    FIRE_CRAWL_API_KEY: process.env.FIRE_CRAWL_API_KEY,
    ATTIO_WEBHOOK_API_SECRET: process.env.ATTIO_WEBHOOK_API_SECRET,
    PEOPLE_DATA_LABS_API_KEY: process.env.PEOPLE_DATA_LABS_API_KEY,
    EXA_API_KEY: process.env.EXA_API_KEY,
    PROXY_CURL_API_KEY: process.env.PROXY_CURL_API_KEY,
    TRIGGER_SECRET_KEY: process.env.TRIGGER_SECRET_KEY,
    SLACK_SIGNING_SECRET: process.env.SLACK_SIGNING_SECRET,
    RECALL_AI_API_KEY: process.env.RECALL_AI_API_KEY,
    RECALL_AI_SIGNING_SECRET: process.env.RECALL_AI_SIGNING_SECRET,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_POSTHOG_ENABLED: process.env.NEXT_PUBLIC_POSTHOG_ENABLED,
    GOOGLE_SHEETS_WRITER_TYPE: process.env.GOOGLE_SHEETS_WRITER_TYPE,
    GOOGLE_SHEETS_WRITER_PROJECT_ID:
      process.env.GOOGLE_SHEETS_WRITER_PROJECT_ID,
    GOOGLE_SHEETS_WRITER_PRIVATE_KEY_ID:
      process.env.GOOGLE_SHEETS_WRITER_PRIVATE_KEY_ID,
    GOOGLE_SHEETS_WRITER_PRIVATE_KEY:
      process.env.GOOGLE_SHEETS_WRITER_PRIVATE_KEY,
    GOOGLE_SHEETS_WRITER_CLIENT_EMAIL:
      process.env.GOOGLE_SHEETS_WRITER_CLIENT_EMAIL,
    GOOGLE_SHEETS_WRITER_CLIENT_ID: process.env.GOOGLE_SHEETS_WRITER_CLIENT_ID,
    GOOGLE_SHEETS_WRITER_AUTH_URI: process.env.GOOGLE_SHEETS_WRITER_AUTH_URI,
    GOOGLE_SHEETS_WRITER_TOKEN_URI: process.env.GOOGLE_SHEETS_WRITER_TOKEN_URI,
    GOOGLE_SHEETS_WRITER_AUTH_PROVIDER_X509_CERT_URL:
      process.env.GOOGLE_SHEETS_WRITER_AUTH_PROVIDER_X509_CERT_URL,
    GOOGLE_SHEETS_WRITER_CLIENT_X509_CERT_URL:
      process.env.GOOGLE_SHEETS_WRITER_CLIENT_X509_CERT_URL,
    GOOGLE_SHEETS_WRITER_UNIVERSE_DOMAIN:
      process.env.GOOGLE_SHEETS_WRITER_UNIVERSE_DOMAIN,
    GOOGLE_CLOUD_PROJECT_ID: process.env.GOOGLE_CLOUD_PROJECT_ID,
    GOOGLE_SERVICE_ACCOUNT_EMAIL: process.env.GOOGLE_SERVICE_ACCOUNT_EMAIL,
    GOOGLE_SERVICE_ACCOUNT_TYPE: process.env.GOOGLE_SERVICE_ACCOUNT_TYPE,
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY_ID:
      process.env.GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY_ID,
    GOOGLE_SERVICE_ACCOUNT_CLIENT_ID:
      process.env.GOOGLE_SERVICE_ACCOUNT_CLIENT_ID,
    GOOGLE_SERVICE_ACCOUNT_AUTH_URI:
      process.env.GOOGLE_SERVICE_ACCOUNT_AUTH_URI,
    GOOGLE_SERVICE_ACCOUNT_TOKEN_URI:
      process.env.GOOGLE_SERVICE_ACCOUNT_TOKEN_URI,
    GOOGLE_SERVICE_ACCOUNT_AUTH_PROVIDER_X509_CERT_URL:
      process.env.GOOGLE_SERVICE_ACCOUNT_AUTH_PROVIDER_X509_CERT_URL,
    GOOGLE_SERVICE_ACCOUNT_X509_CERT_URL:
      process.env.GOOGLE_SERVICE_ACCOUNT_X509_CERT_URL,
    GOOGLE_SERVICE_ACCOUNT_UNIVERSE_DOMAIN:
      process.env.GOOGLE_SERVICE_ACCOUNT_UNIVERSE_DOMAIN,
    GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY:
      process.env.GOOGLE_SERVICE_ACCOUNT_PRIVATE_KEY,
    OUTLOOK_NOTIFICATION_SECRET: process.env.OUTLOOK_NOTIFICATION_SECRET,
    GRAPH_TENANT_ID: process.env.GRAPH_TENANT_ID,
    GRAPH_CLIENT_ID: process.env.GRAPH_CLIENT_ID,
    GRAPH_CLIENT_SECRET: process.env.GRAPH_CLIENT_SECRET,
    MOTHER_DUCK_ACCESS_TOKEN: process.env.MOTHER_DUCK_ACCESS_TOKEN,
    MISTRAL_API_KEY: process.env.MISTRAL_API_KEY,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   * This is especially useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
});
